import { createAsyncThunk } from '@reduxjs/toolkit'
import { baseUrl, headersDefault } from '.'
import { fetchAppPermissions } from './permissionsActions'

export const getProducts = createAsyncThunk(
	'subscriptions/getProducts',
	async () => {
		const response = await fetch(`${baseUrl}/api/selling-subscription/list`, {
			method: 'GET',
			headers: headersDefault(),
		})

		if (response.status !== 200)
			throw new Error(
				(response.message || (await response.json()).error) ?? 'GenericError'
			)

		return await response.json()
	}
)

export const createOrder = createAsyncThunk(
	'subscriptions/createOrder',
	async (settings) => {
		const response = await fetch(settings.create_order_url, {
			method: 'POST',
			body: JSON.stringify({
				product_id: settings.product_id,
				subscriber_id: settings.subscriber_id,
			}),
			headers: headersDefault(),
		})

		if (response.status !== 200)
			throw new Error(
				(response.message || (await response.json()).error) ?? 'GenericError'
			)

		return await response.json()
	}
)

export const cancelAtPeriodEnd = createAsyncThunk(
	'subscriptions/cancelAtPeriodEnd',
	async ({ subscriptionId, value }) => {
		const response = await fetch(
			`${baseUrl}/api/master-club-subscriptions/${subscriptionId}`,
			{
				method: 'PATCH',
				body: JSON.stringify({ cancel_at_period_end: value }),
				headers: headersDefault(),
			}
		)

		if (response.status !== 200)
			throw new Error(
				(response.message || (await response.json()).error) ?? 'GenericError'
			)

		return await response.json()
	}
)

export const cancelFreeSubscription = createAsyncThunk(
	'subscriptions/cancelFreeSubscription',
	async (_, { dispatch }) => {
		const response = await fetch(`${baseUrl}/api/master-club-subscriptions`, {
			method: 'POST',
			body: JSON.stringify({
				cancel_free_subscription: true,
			}),
			headers: headersDefault(),
		})

		if (response.status !== 200) {
			// The backend explains a refusal in `message` (e.g. the module that
			// blocks cancelling); reading only `error` showed "GenericError".
			const body = await response.json().catch(() => ({}))
			throw new Error(body?.message || body?.error || 'GenericError')
		}

		const data = await response.json()
		dispatch(fetchAppPermissions())
		return data
	}
)

export const startFreeSubscription = createAsyncThunk(
	'subscriptions/startFreeSubscription',
	async (freeRouteStartDate, { dispatch }) => {
		const response = await fetch(`${baseUrl}/api/master-club-subscriptions`, {
			method: 'POST',
			body: JSON.stringify({
				free_route_start_date: freeRouteStartDate,
			}),
			headers: headersDefault(),
		})

		const data = await response.json()
		const ok = response.status === 200
		const hasActiveRoute =
			typeof data?.message === 'string' &&
			data.message.includes('percorso attivo')
		// The phone-verification gate is a normal, expected refusal, not an
		// error: the caller reacts by opening the verification popup. Throwing
		// would send it to a silent catch and the user would see nothing happen.
		const needsPhoneVerification = data?.phone_verification_required === true

		if (ok || hasActiveRoute) {
			dispatch(fetchAppPermissions())
		}

		if (!ok && !hasActiveRoute && !needsPhoneVerification) {
			throw new Error(data?.message || data?.error || 'GenericError')
		}

		return { data, ok }
	}
)

/**
 * Reads the user's currently-active entitlements from the backend's
 * /api/v1/me/entitlements endpoint. The native paywall calls this after a
 * successful StoreKit/Play Billing purchase to refresh local premium-status
 * state without round-tripping the SDK's local cache.
 */
export const getEntitlements = createAsyncThunk(
	'subscriptions/getEntitlements',
	async () => {
		const response = await fetch(`${baseUrl}/api/v1/me/entitlements`, {
			method: 'GET',
			headers: headersDefault(),
		})

		if (response.status !== 200)
			throw new Error(
				(response.message || (await response.json()).error) ?? 'GenericError'
			)

		return await response.json()
	}
)

export const updatePaymentMethod = createAsyncThunk(
	'subscriptions/updatePaymentMethod',
	async () => {
		const response = await fetch(`${baseUrl}/api/payment/update-method`, {
			method: 'GET',
			headers: headersDefault(),
		})

		if (response.status !== 200)
			throw new Error(
				(response.message || (await response.json()).error) ?? 'GenericError'
			)

		return await response.json()
	}
)
